import { MAX_TENANT_API_RATE_LIMIT, type TenantApiRateLimit } from "@repo/shared/types";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api/_client";
import { useUpdateTenantApiRateLimit } from "@/lib/hooks/use-admin";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./tenant-api-rate-limit-card.i18n";

type TenantApiRateLimitCardProps = {
  tenantId: string;
  apiRateLimit: TenantApiRateLimit;
  /** Quantfolio edits; partners see the value read-only (the API enforces it too). */
  canEdit: boolean;
};

/**
 * QL-359 — the tenant's public API allowance: requests per fixed window, all of
 * its API keys combined. Only the request count is configurable, only by Quantfolio.
 */
export function TenantApiRateLimitCard({
  tenantId,
  apiRateLimit,
  canEdit,
}: TenantApiRateLimitCardProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const formatCount = (n: number) => n.toLocaleString(userLanguage.replace("_", "-"));
  const [draft, setDraft] = useState(String(apiRateLimit.override ?? ""));
  const [error, setError] = useState<string | null>(null);

  const mutation = useUpdateTenantApiRateLimit(tenantId);
  const save = (limit: number | null) =>
    mutation.mutate(limit, {
      onSuccess: (saved) => {
        setError(null);
        toast.success(saved.override === null ? t.reset : t.saved);
        setDraft(String(saved.override ?? ""));
      },
      onError: (err) => {
        setError(err instanceof ApiError && err.message ? err.message : t.saveFailed);
      },
    });

  const parsed = Number(draft);
  // UX only — mirrors the API's rule (a whole number from 1 to the maximum),
  // which the API enforces itself.
  const draftIsValid =
    draft.trim() !== "" &&
    Number.isInteger(parsed) &&
    parsed >= 1 &&
    parsed <= MAX_TENANT_API_RATE_LIMIT;
  const unchanged = draftIsValid && parsed === apiRateLimit.override;
  const isOverride = apiRateLimit.override !== null;
  const perMinute = apiRateLimit.windowSeconds === 60;
  const seconds = String(apiRateLimit.windowSeconds);
  const windowLabel = perMinute ? t.requestsPerMinute : t.requestsPerSeconds(seconds);
  const inputLabel = perMinute ? t.inputPerMinute : t.inputPerSeconds(seconds);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-2xl font-semibold tabular-nums">
            {formatCount(apiRateLimit.limit)}
          </span>
          <span className="text-sm text-muted-foreground">{windowLabel}</span>
          <Badge variant={isOverride ? "info" : "secondary"}>
            {isOverride ? t.override : t.default}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          {t.sharedPrefix} {formatCount(apiRateLimit.defaultLimit)} {t.requests}
          {isOverride ? t.ownAllowance : t.inheritsDefault}
        </p>

        {canEdit ? (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (draftIsValid && !unchanged) save(parsed);
            }}
          >
            <div className="space-y-1">
              <Label htmlFor="api-rate-limit">{inputLabel}</Label>
              <Input
                id="api-rate-limit"
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_TENANT_API_RATE_LIMIT}
                step={1}
                className="w-40"
                placeholder={String(apiRateLimit.defaultLimit)}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setError(null);
                }}
                disabled={mutation.isPending}
              />
            </div>
            <Button
              type="submit"
              disabled={
                !draftIsValid || unchanged || (mutation.isPending && mutation.variables === null)
              }
              loading={mutation.isPending && mutation.variables !== null}
            >
              {t.save}
            </Button>
            {isOverride && (
              <Button
                type="button"
                variant="outline"
                disabled={mutation.isPending}
                loading={mutation.isPending && mutation.variables === null}
                onClick={() => save(null)}
              >
                {t.resetToDefault}
              </Button>
            )}
          </form>
        ) : (
          <p className="text-sm text-muted-foreground">{t.managedByQuantfolio}</p>
        )}

        {error && (
          <div role="alert" className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
